import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Download, FileText, Archive, Shield, CheckCircle, 
  Copy, Check, ChevronDown, ChevronUp, ExternalLink,
  RefreshCw
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, API } from "@/lib/api";

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (isoString) => {
  if (!isoString) return "N/A";
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short"
    });
  } catch {
    return isoString;
  }
};

const HashCopyButton = ({ hash, label }) => {
  const [copied, setCopied] = useState(false);
  
  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="flex items-center gap-2">
      <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded break-all">
        {hash.slice(0, 16)}...
      </code>
      <button
        onClick={copyToClipboard}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        title={`Copy full ${label}`}
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <Copy className="w-4 h-4 text-gray-500" />
        )}
      </button>
    </div>
  );
};

const IntegrityGuide = () => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="mt-8 border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 md:px-6 py-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="font-semibold serif-heading">How to Verify Archive Integrity</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      
      {expanded && (
        <div className="px-4 md:px-6 pb-6 border-t border-gray-100 pt-4">
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">
              Each archive pack includes cryptographic hashes for verification. The <strong>file hash</strong> is 
              a SHA-256 checksum of the downloaded PDF file.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium mb-2">Linux/macOS:</p>
              <code className="block bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto">
                sha256sum downloaded-archive.pdf
              </code>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium mb-2">Windows (PowerShell):</p>
              <code className="block bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto">
                Get-FileHash downloaded-archive.pdf -Algorithm SHA256
              </code>
            </div>
            
            <p className="text-gray-600">
              Compare the output with the <strong>File Hash</strong> shown in the table. If they match, 
              the file has not been modified since generation.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> The content hash is computed from the document content before 
                PDF conversion. Different versions with the same content hash indicate no substantive 
                changes to the underlying data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PackTypeSection = ({ packType, data, onGenerate, generating }) => {
  const [showAllVersions, setShowAllVersions] = useState(false);
  
  const versions = data.versions || [];
  const displayVersions = showAllVersions ? versions : versions.slice(0, 3);
  const hasMoreVersions = versions.length > 3;
  
  const packTypeMap = {
    "tickets-archive": { icon: "🎫", accent: "blue" },
    "facts-index": { icon: "⚖️", accent: "orange" },
    "core-evidence": { icon: "📋", accent: "green" }
  };
  
  const config = packTypeMap[packType] || { icon: "📄", accent: "gray" };
  
  return (
    <div className="archive-card overflow-hidden" data-testid={`archive-pack-${packType}`}>
      <div className="p-4 md:p-6 border-b border-gray-100">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 md:gap-4 min-w-0">
            <span className="text-2xl md:text-3xl flex-shrink-0">{config.icon}</span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg md:text-xl font-bold serif-heading">{data.name}</h2>
              <p className="text-gray-600 mt-1 text-sm">{data.description}</p>
              {data.last_changelog && data.last_changelog !== "N/A" && (
                <p className="text-blue-600 text-sm mt-2">
                  <span className="font-medium">Latest changes:</span> {data.last_changelog}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => onGenerate(packType)}
            disabled={generating === packType}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 disabled:opacity-50 transition-colors w-full md:w-auto flex-shrink-0"
            data-testid={`generate-${packType}-btn`}
          >
            {generating === packType ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Generate New</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {versions.length > 0 ? (
        <>
          {/* Mobile card layout - visible only below md (768px) */}
          <div className="md:hidden divide-y divide-gray-100">
            {displayVersions.map((version, idx) => (
              <div key={version.filename} className={`p-4 ${idx === 0 ? "bg-green-50" : ""}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {idx === 0 && (
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200 text-xs">
                        Latest
                      </Badge>
                    )}
                    <Badge variant="outline" className="font-mono text-xs">v{version.pack_version}</Badge>
                  </div>
                  <span className="text-gray-600 text-xs">{formatFileSize(version.file_size_bytes)}</span>
                </div>
                <p className="text-xs text-gray-600 mb-2">{formatDate(version.generated_at_utc)}</p>
                {version.changelog && (
                  <p className="text-sm text-gray-700 mb-3">{version.changelog}</p>
                )}
                <a
                  href={`${API}${version.download_url}`}
                  download
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-sm"
                  data-testid={`download-mobile-${version.filename}`}
                >
                  <FileText className="w-4 h-4" />
                  Download PDF
                </a>
              </div>
            ))}
          </div>
          
          {/* Desktop table layout - visible only at md (768px) and above */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Generated</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Changes</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Size</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">File Hash</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayVersions.map((version, idx) => (
                  <tr key={version.filename} className={idx === 0 ? "bg-green-50" : ""}>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {idx === 0 && (
                            <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200 text-xs">
                              Latest
                            </Badge>
                          )}
                          <Badge variant="outline" className="font-mono text-xs">v{version.pack_version}</Badge>
                        </div>
                        <span className="text-gray-900 text-xs">{formatDate(version.generated_at_utc)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {version.changelog ? (
                        <span className="text-gray-700 text-sm">{version.changelog}</span>
                      ) : (
                        <span className="text-gray-400 text-sm italic">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatFileSize(version.file_size_bytes)}
                    </td>
                    <td className="px-4 py-3">
                      <HashCopyButton hash={version.file_hash} label="file hash" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={`${API}${version.download_url}`}
                        download
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                        data-testid={`download-${version.filename}`}
                      >
                        <FileText className="w-4 h-4" />
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hasMoreVersions && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setShowAllVersions(!showAllVersions)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showAllVersions 
                  ? "Show fewer versions" 
                  : `Show all ${versions.length} versions`
                }
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center text-gray-500">
          <Archive className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No versions generated yet.</p>
          <p className="text-sm mt-1">Click "Generate New" to create the first archive pack.</p>
        </div>
      )}
    </div>
  );
};

export const ArchiveDownloadsPage = () => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState(null);
  
  const loadCatalog = async () => {
    try {
      const res = await api.get("/archives");
      setCatalog(res.data);
      setError(null);
    } catch (err) {
      console.error("Failed to load archive catalog", err);
      setError("Failed to load archive catalog");
    }
    setLoading(false);
  };
  
  useEffect(() => {
    loadCatalog();
  }, []);
  
  const handleGenerate = async (packType) => {
    setGenerating(packType);
    
    const endpoints = {
      "tickets-archive": "/pdf/archive/tickets",
      "facts-index": "/pdf/archive/facts",
      "core-evidence": "/pdf/archive/core-evidence"
    };
    
    try {
      // Trigger generation by calling the endpoint
      const response = await fetch(`${API}${endpoints[packType]}`);
      if (response.ok) {
        // Download the file
        const blob = await response.blob();
        const filename = response.headers.get("Content-Disposition")?.split("filename=")[1]?.replace(/"/g, "") || `${packType}.pdf`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        // Reload catalog to show new version
        await loadCatalog();
      }
    } catch (err) {
      console.error("Generation failed", err);
    }
    
    setGenerating(null);
  };
  
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="archive-downloads-page">
      <div className="mb-10">
        <h1 className="text-3xl font-bold serif-heading mb-4">Archive Downloads</h1>
        <p className="text-gray-600 leading-relaxed max-w-3xl">
          Download complete archive packs for offline access, institutional deposit, or scholarly citation. 
          Each pack includes integrity verification hashes and generation timestamps.
        </p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      <div className="space-y-8">
        {catalog && Object.entries(catalog).map(([packType, data]) => (
          <PackTypeSection
            key={packType}
            packType={packType}
            data={data}
            onGenerate={handleGenerate}
            generating={generating}
          />
        ))}
      </div>
      
      <IntegrityGuide />
      
      <div className="mt-10 p-4 md:p-6 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold serif-heading text-blue-900 mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          Citation Guidelines
        </h3>
        <p className="text-sm text-blue-800 mb-3">
          When citing archive packs in scholarly work, include the following information:
        </p>
        <div className="bg-white p-3 md:p-4 rounded border border-blue-200 text-xs md:text-sm font-mono break-words overflow-wrap-anywhere">
          Elvis Aloha 1973 Historical Archive. "[Pack Name]." Version [N]. Generated [Date]. 
          File Hash: [First 16 chars]. https://elvisaloha1973.com/archives
        </div>
        <p className="text-xs text-blue-700 mt-3">
          Including the file hash ensures your citation references the exact version you consulted.
        </p>
      </div>
      
      <div className="mt-8 text-center">
        <Link to="/governance" className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1">
          View Archive Versioning Policy
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default ArchiveDownloadsPage;
